import axios from 'axios'
import toast from 'react-hot-toast'

// Create axios instance
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'https://api.mymargdarshak.com/api/v1',
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
    paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => searchParams.append(key, v));
            } else if (value !== null && value !== undefined && value !== '') {
                searchParams.append(key, value);
            }
        });
        return searchParams.toString();
    }
})

// Request interceptor
api.interceptors.request.use(
    (config) => {
        // Add auth token if available
        const token = localStorage.getItem('admin_token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor
api.interceptors.response.use(
    (response) => {
        return response.data
    },
    (error) => {
        const message = error.response?.data?.message || error.message || 'An error occurred'

        // Handle specific error cases
        if (error.response?.status === 401) {
            localStorage.removeItem('admin_token')
            localStorage.removeItem('admin_user')
            window.location.href = '/admin'
        }

        // Show error toast for non-401 errors
        if (error.response?.status !== 401) {
            toast.error(message)
        }

        return Promise.reject(error)
    }
)

// API methods
export const homeAPI = {
    getHomeData: () => api.get('/home'),
    getStats: () => api.get('/home/stats'),
}

export const universityAPI = {
    getUniversities: (params) => api.get('/universities', { params }),
    getUniversity: (slug) => api.get(`/universities/${slug}`),
    getUniversityCourses: (id, params) => api.get(`/universities/${id}/courses`, { params }),
    getCities: () => api.get('/universities/cities/list'),
    getStates: () => api.get('/universities/states/list'),

    // Comprehensive detail endpoints
    getUniversityOverview: (id) => api.get(`/universities/${id}/overview`),
    getUniversityCoursesFees: (id) => api.get(`/universities/${id}/courses-fees`),
    getUniversityAdmission: (id) => api.get(`/universities/${id}/admission`),
    getUniversityPlacements: (id) => api.get(`/universities/${id}/placements`),
    getUniversityScholarships: (id) => api.get(`/universities/${id}/scholarships`),
    getUniversityGallery: (id) => api.get(`/universities/${id}/gallery`),
    getUniversityReviews: (id, params) => api.get(`/universities/${id}/reviews`, { params }),
    addUniversityReview: (id, data) => api.post(`/universities/${id}/reviews`, data),

    // Admin methods
    getAdminUniversities: (params) => api.get('/universities/admin', { params }),
    createUniversity: (data) => api.post('/universities', data),
    updateUniversity: (id, data) => api.put(`/universities/${id}`, data),
    deleteUniversity: (id) => api.delete(`/universities/${id}`),

    // University course management
    getUniversityCourses: (universityId) => api.get(`/universities/${universityId}/courses`),
    createUniversityCourse: (universityId, data) => api.post(`/universities/${universityId}/courses`, data),
    updateUniversityCourse: (universityId, courseId, data) => api.put(`/universities/${universityId}/courses/${courseId}`, data),
    deleteUniversityCourse: (universityId, courseId) => api.delete(`/universities/${universityId}/courses/${courseId}`),
}

export const courseAPI = {
    getCourses: (params) => api.get('/courses', { params }),
    getCourse: (slug) => api.get(`/courses/${slug}`),
    getCategories: () => api.get('/courses/categories/list'),
    getLevels: () => api.get('/courses/levels/list'),
    getModes: () => api.get('/courses/modes/list'),
    getFeeRange: () => api.get('/courses/fee-range'),

    // Comprehensive detail endpoints
    getCourseOverview: (id) => api.get(`/courses/${id}/overview`),
    getCourseCurriculum: (id) => api.get(`/courses/${id}/curriculum`),
    getCourseEligibility: (id) => api.get(`/courses/${id}/eligibility`),
    getCourseFeeStructure: (id) => api.get(`/courses/${id}/fee-structure`),
    getCoursePlacements: (id) => api.get(`/courses/${id}/placements`),
    getCourseScholarships: (id) => api.get(`/courses/${id}/scholarships`),

    // Admin methods
    getAdminCourses: (params) => api.get('/admin/courses', { params }),
    createCourse: (data) => api.post('/courses', data),
    updateCourse: (id, data) => api.put(`/courses/${id}`, data),
    deleteCourse: (id) => api.delete(`/courses/${id}`),
}

export const enquiryAPI = {
    createEnquiry: (data) => api.post('/enquiries', data),
    // Admin methods
    getEnquiries: (params) => api.get('/admin/enquiries', { params }),
    getEnquiry: (id) => api.get(`/enquiries/${id}`),
    updateEnquiry: (id, data) => api.put(`/enquiries/${id}`, data),
    updateEnquiryStatus: (id, status) => api.put(`/enquiries/${id}/status`, { status }),
    addNote: (id, data) => api.post(`/enquiries/${id}/notes`, data),
    getStats: () => api.get('/enquiries/stats/overview'),
    exportEnquiries: (params) => api.get('/admin/enquiries/export', {
        params,
        responseType: 'blob'
    }),
}

export const bannerAPI = {
    getBanners: (params) => api.get('/banners', { params }),
    getBanner: (id) => api.get(`/banners/${id}`),
    trackClick: (id) => api.post(`/banners/${id}/click`),
    trackImpression: (id) => api.post(`/banners/${id}/impression`),
    getPositions: () => api.get('/banners/positions/list'),
    getStats: () => api.get('/banners/stats/overview'),
    // Admin methods
    createBanner: (data) => api.post('/banners', data),
    updateBanner: (id, data) => api.put(`/banners/${id}`, data),
    deleteBanner: (id) => api.delete(`/banners/${id}`),
}

export const faqAPI = {
    getFAQs: (params) => api.get('/faqs', { params }),
    getFAQ: (id) => api.get(`/faqs/${id}`),
    markHelpful: (id) => api.post(`/faqs/${id}/helpful`),
    markNotHelpful: (id) => api.post(`/faqs/${id}/not-helpful`),
    getCategories: () => api.get('/faqs/categories/list'),
    getStats: () => api.get('/faqs/stats/overview'),
    // Admin methods
    createFAQ: (data) => api.post('/faqs', data),
    updateFAQ: (id, data) => api.put(`/faqs/${id}`, data),
    deleteFAQ: (id) => api.delete(`/faqs/${id}`),
}

export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    getProfile: () => api.get('/auth/me'),
    updateProfile: (data) => api.put('/auth/me', data),
    changePassword: (data) => api.put('/auth/change-password', data),
    forgotPassword: (data) => api.post('/auth/forgot-password', data),
    resetPassword: (data) => api.post('/auth/reset-password', data),
    logout: () => api.post('/auth/logout'),
    getUsers: () => api.get('/auth/users'),
}

export const adminAPI = {
    getDashboard: () => api.get('/admin/dashboard'),
    getAnalytics: (params) => api.get('/admin/analytics', { params }),
}

export const settingsAPI = {
    getSettings: () => api.get('/admin/settings'),
    updateSettings: (data) => api.put('/admin/settings', data),
    getGeneralSettings: () => api.get('/admin/settings/general'),
    updateGeneralSettings: (data) => api.put('/admin/settings/general', data),
    getContactSettings: () => api.get('/admin/settings/contact'),
    updateContactSettings: (data) => api.put('/admin/settings/contact', data),
    getEmailSettings: () => api.get('/admin/settings/email'),
    updateEmailSettings: (data) => api.put('/admin/settings/email', data),
    getSecuritySettings: () => api.get('/admin/settings/security'),
    updateSecuritySettings: (data) => api.put('/admin/settings/security', data),
    getNotificationSettings: () => api.get('/admin/settings/notifications'),
    updateNotificationSettings: (data) => api.put('/admin/settings/notifications', data),
}

export const counsellorAPI = {
    getCounsellors: (params) => api.get('/counsellors', { params }),
    getCounsellor: (id) => api.get(`/counsellors/${id}`),
    getFeatured: () => api.get('/counsellors/featured'),
    getByCity: (city) => api.get(`/counsellors/city/${city}`),
    // Admin methods
    createCounsellor: (data) => api.post('/counsellors', data),
    updateCounsellor: (id, data) => api.put(`/counsellors/${id}`, data),
    deleteCounsellor: (id) => api.delete(`/counsellors/${id}`),
}

// File upload helper
export const uploadFile = async (file, type = 'image') => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)

    return api.post('/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    })
}

// Download file helper
export const downloadFile = async (url, filename) => {
    try {
        const response = await fetch(url)
        const blob = await response.blob()
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
        toast.error('Failed to download file')
    }
}

// Approvals API
export const approvalAPI = {
    getApprovals: (params = {}) => api.get('/approvals', { params }),
    getApproval: (id) => api.get(`/approvals/${id}`),
    createApproval: (data) => api.post('/approvals', data),
    updateApproval: (id, data) => api.put(`/approvals/${id}`, data),
    deleteApproval: (id) => api.delete(`/approvals/${id}`),
    getAdminApprovals: (params = {}) => api.get('/approvals/admin', { params })
}

// Specializations API
export const specializationAPI = {
    getSpecializations: (params = {}) => api.get('/specializations', { params }),
    getSpecialization: (id) => api.get(`/specializations/${id}`),
    createSpecialization: (data) => api.post('/specializations', data),
    updateSpecialization: (id, data) => api.put(`/specializations/${id}`, data),
    deleteSpecialization: (id) => api.delete(`/specializations/${id}`),
    getAdminSpecializations: (params = {}) => api.get('/specializations/admin', { params })
}

// Degrees API
export const degreeAPI = {
    getDegrees: (params = {}) => api.get('/degrees', { params }),
    getDegree: (id) => api.get(`/degrees/${id}`),
    getDegreeSpecializations: (id) => api.get(`/degrees/${id}/specializations`),
    createDegree: (data) => api.post('/degrees', data),
    updateDegree: (id, data) => api.put(`/degrees/${id}`, data),
    deleteDegree: (id) => api.delete(`/degrees/${id}`),
    getAdminDegrees: (params = {}) => api.get('/degrees/admin', { params })
}

// Blogs API
export const blogAPI = {
    // Public methods
    getBlogs: (params) => api.get('/blogs', { params }),
    getBlog: (slug) => api.get(`/blogs/${slug}`),
    getFeaturedBlogs: (params) => api.get('/blogs/featured/list', { params }),
    getRecentBlogs: (params) => api.get('/blogs/recent/list', { params }),
    getPopularBlogs: (params) => api.get('/blogs/popular/list', { params }),
    getCategories: () => api.get('/blogs/categories/list'),
    getTags: () => api.get('/blogs/tags/list'),
    shareBlog: (id) => api.post(`/blogs/${id}/share`),

    // Admin methods
    getAdminBlogs: (params) => api.get('/blogs/admin/list', { params }),
    getAdminBlog: (id) => api.get(`/blogs/admin/${id}`),
    createBlog: (data) => api.post('/blogs/admin/create', data),
    updateBlog: (id, data) => api.put(`/blogs/admin/${id}`, data),
    deleteBlog: (id) => api.delete(`/blogs/admin/${id}`),
    updateBlogStatus: (id, status) => api.put(`/blogs/admin/${id}/status`, { status }),
    toggleFeatured: (id) => api.put(`/blogs/admin/${id}/featured`)
}

export const adminUserAPI = {
    // Get all admin users with pagination and filters
    getAdminUsers: (params) => api.get('/admin-users', { params }),

    // Get single admin user
    getAdminUser: (id) => api.get(`/admin-users/${id}`),

    // Create new admin user
    createAdminUser: (data) => api.post('/admin-users', data),

    // Update admin user
    updateAdminUser: (id, data) => api.put(`/admin-users/${id}`, data),

    // Delete admin user
    deleteAdminUser: (id) => api.delete(`/admin-users/${id}`),

    // Toggle user active status
    toggleUserStatus: (id) => api.put(`/admin-users/${id}/toggle-status`),

    // Get user statistics
    getUserStats: () => api.get('/admin-users/stats/overview')
}

export default api
